import { UI_PRIMITIVE__input } from '@/components/ui/UI_PRIMITIVE__input';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { Key } from '@/enums/key';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_search, use_slice_workspace } from '@/hooks/store/use_slices';
import i18n_page_search from '@/locales/keys/i18n_page_search.json';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_search_token_add } from '@/state/search/tokens/remote_search_token_add';
import { remote_search_tokens_update_visibility } from '@/state/search/tokens/remote_search_tokens_update_visibility';
import { for_token_add_create_request, for_token_add_create_word_with_context } from '@/utils/utils_tokens';
import { RequestTokensUpdateVisibility } from '@shared/common/_requests/request_tokens_update_visibility';
import { CONSTANTS_GENERATION__value_default } from '@shared/constants/CONSTANTS_GENERATION__value_default';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { DTO__token } from '@shared/dto/generation/settings/token/DTO__token';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function SPromptInput() {
  const dispatch = use_dispatch();

  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_SEARCH);

  const { search_current } = use_slice_search(['search_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);

  const [token_component_id, set_token_component_id] = useState('');
  const token_notifier = HOOK__notifier(token_component_id, ENUM__workspace_entity.ENTITY_SEARCH);

  const [inputValue, setInputValue] = useState('');
  const [is_loading, set_is_loading] = useState(false);

  /*
   * EFFECTS
   */
  use_effect(() => {
    if (token_component_id) void token_notifier.send_action();
  }, [token_component_id]);

  /*
   * EVENTS
   */

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    let valueString = e.target.value.toLowerCase();

    setInputValue(valueString);
  }

  async function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key != Key.Enter) return;

    let valueString = inputValue;
    setInputValue('');

    let tokens_to_show = [];
    let request = for_token_add_create_request(search_current._id);

    const tokens_new = string_to_tokens(valueString);

    for (let token_new of tokens_new) {
      const token_saved = search_current.tokens.find(
        token_saved => token_saved.word.word_translated === token_new.word_english || token_saved.word_english === token_new.word_english,
      );

      if (token_saved) {
        tokens_to_show.push(token_saved);
      } else {
        let token_create_data = for_token_add_create_word_with_context(
          token_new.word_english,
          CONSTANTS_GENERATION__value_default.TOKEN_WEIGHT,
          search_current.tokens,
          !e.shiftKey,
        );

        request.tokens_data.push(token_create_data);
      }
    }

    set_is_loading(true);

    await try_show_tokens(tokens_to_show);

    await dispatch(remote_search_token_add(request));

    set_is_loading(false);

    for (const token of tokens_new) {
      if (search_current.tokens.some(t => t.word_english === token.word_english)) continue;

      set_token_component_id(`${ENUM__component_type.PAGE_SEARCH_TOKEN_BAR}_${token.word_english.toUpperCase()}_${search_current._id}`);
    }
  }

  /*
   * METHODS
   */

  async function try_show_tokens(tokens: DTO__token[]) {
    tokens = tokens.filter(token => !token.is_hidden);

    if (tokens.length === 0) return;

    tokens = tokens.map(token => token.clone({ is_hidden: false }));

    let request = new RequestTokensUpdateVisibility(search_current?._id);
    request.tokens = tokens;

    await dispatch(remote_search_tokens_update_visibility(request));
  }

  function is_disabled() {
    return is_loading;
  }

  /*
   * HTML
   */

  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_SEARCH_UPDATE)) return <PLACEHOLDER_bar_line />;

  return (
    <div className="w-full px-2 py-2">
      <UI_PRIMITIVE__input
        disabled={is_disabled()}
        className="rounded-lg border-none px-4 h-9 placeholder:text-component-item"
        placeholder={t(i18n_page_search.sidebar.prompt_component.input.token.placeholder)}
        value={inputValue}
        {...{ onChange, onKeyDown }}
      />
    </div>
  );
}
